import Input from '@atomaro/ui-kit/components/Input/Input';
import {LogoSvg} from 'shared/ui/logoSvg';
import {MarginBottom} from 'shared/ui/marginBottom';
import {Switchers} from './switchers';

import {BlockedMessage, Container, LoginWindow, RecoveryButton, Title} from './styles';

import {Controller, ControllerRenderProps, useForm} from 'react-hook-form';
import {useHookFormMask} from 'use-mask-input';

import {Button} from '@atomaro/ui-kit';
import {ChangeEvent, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {unmaskPhone} from 'shared/helpers';
import {PassHide, PassShow} from 'shared/ui/icons';
import {ConfigContext} from '../../../app/App.tsx';

type formValues = {
  email?: string;
  phone?: string;
  password: string;
};

const MANAGER_URL = 'https://max.ru/se13317967_1_bot';

export const Login = () => {
  const {realm} = useParams();
  const [searchParams] = useSearchParams();
  const redirectUri = searchParams.get('redirect_uri');

  const config = useContext(ConfigContext);

  const [showPass, setShowPass] = useState<boolean>(false);
  const [type, setType] = useState<string>('email');
  const [isBlockedError, setIsBlockedError] = useState<boolean>(false);

  const navigate = useNavigate();

  const ALLOWED_REDIRECTS = [
    config.VITE_ADMIN_HOST,
    'https://admin.oktema.ru/users',
    'https://zhuzhu.oktema.ru/auth/callback',
    'http://localhost:3000/auth/callback',
    `${config.VITE_BEESHARE_HOST}/auth/callback`,
    'https://%D1%82%D0%B2%D0%BE%D0%B9%D1%83%D0%BB%D0%B5%D0%B9.%D1%80%D1%84/auth/callback',
    'http://ecpp2.net.bashtel.ru',
    'https://xn--b1aglbiq0al.xn--p1ai/auth/callback',
  ];
  if (redirectUri && ALLOWED_REDIRECTS.includes(redirectUri)) {
    sessionStorage.setItem('externalRedirect', redirectUri);
  }

  const {
    control,
    formState: {errors},
    register,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<formValues>({
    defaultValues: {
      email: '',
      phone: '',
      password: '',
    },
  });

  const externalRedirect = sessionStorage.getItem('externalRedirect');

  const registerWithMask = useHookFormMask(register);
  const phoneMask = useRef('+7 (999) 999-99-99');
  const phoneInputRef = registerWithMask('phone', [phoneMask.current], {
    required: type === 'phone',
    // eslint-disable-next-line
    pattern: /^((8|\+7)[\- ]?)?(\(?\d{3}\)?[\- ]?)?[\d\- ]{7,10}$/,
    inputmode: 'numeric',
    clearMaskOnLostFocus: true,
    onBlur: (event) => {
      event.target.blur();
      event.target.placeholder = '';
    },
    validate: (value) => {
      if (!value) {
        return true;
      }
      if (unmaskPhone(value).length !== 11) {
        return 'Некорректный номер телефона';
      }
    },
    onChange: (event) => {
      if (event.target.value.startsWith('+7 (89') || event.target.value.startsWith('+7 (7')) {
        setValue('phone', '+7 (9');
      }
    },
    onBeforePaste: (pastedValue) => {
      if (pastedValue.startsWith('8')) {
        return `+${pastedValue.replace('8', '7')}`;
      }

      if (!pastedValue.startsWith('+')) {
        return `+${pastedValue}`;
      }

      return pastedValue;
    },
  });

  const formAction = useMemo(() => {
    const search = `?response_type=code&client_id=web&redirect_uri=${externalRedirect ? externalRedirect : window.location.origin}/&frontend_url=${window.location.origin}`;
    return (
      `${config.VITE_AUTH_HOST || import.meta.env.VITE_AUTH_HOST}/ecpp/oauth/realms/${realm}/authenticate` +
      search
    );
  }, [realm, externalRedirect]);

  const changeEmail = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'email'>
  ) => {
    field.onChange(event);
  };

  const changePass = (
    event: ChangeEvent<HTMLInputElement>,
    field: ControllerRenderProps<formValues, 'password'>
  ) => {
    field.onChange(event);
  };

  const onSubmit = (data: formValues) => {
    let login;
    if (type === 'phone') {
      if (!data.phone) return setError('phone', {message: 'Невалидный номер телефона'});

      if (data?.phone) login = unmaskPhone(data.phone);
    } else {
      if (!data.email) return setError('email', {message: 'Невалидный Email'});

      login = data.email;
    }

    if (!data.password) return setError('password', {message: 'Пароль обязателен'});

    const form = document.createElement('form');
    form.action = formAction;
    form.method = 'POST';
    form.innerHTML = `
      <input type="text" name="login" value='${login}' />
      <input type="text" name="password" value='${data.password}' />
    `;

    document.body.append(form);
    form.submit();
    form.remove();
  };

  useEffect(() => {
    const errors = searchParams.get('errors');
    setIsBlockedError(false);

    if (errors) {
      const errorsArr = errors.split(',');
      for (let index = 0; index < errorsArr.length; index++) {
        const error = errorsArr[index];
        const errorArr = error.split(':');

        let name = errorArr[0];
        if (name === 'account' && errorArr[1] === 'blocked') {
          setIsBlockedError(true);
          continue;
        }
        if (name === 'login') name = 'phone';
        setError(name as any, {message: errorArr[1]});
        // toast.error(error)
      }
    }
  }, [searchParams]);

  return (
    <Container>
      <LoginWindow action={formAction} method="POST">
        <MarginBottom $value={32}>
          <LogoSvg width={40} height={40} />
        </MarginBottom>
        <Title>Вход в аккаунт</Title>

        {isBlockedError && (
          <BlockedMessage>
            Доступ временно заблокирован, для разблокировки обратитесь{' '}
            <a href={MANAGER_URL} target="_blank" rel="noreferrer">
              к менеджеру
            </a>
          </BlockedMessage>
        )}

        <Switchers type={type} setType={setType} />

        {type === 'phone' && (
          <MarginBottom $value={20} style={{width: '100%'}}>
            <Input
              {...phoneInputRef}
              autoComplete="tel"
              size="l"
              label="Номер телефона"
              error={
                errors?.phone
                  ? errors.phone?.type === 'required'
                    ? 'Введите номер телефона'
                    : errors.phone?.type === 'pattern'
                      ? 'Некорректный номер телефона'
                      : errors.phone?.message || 'Некорректный номер телефона'
                  : undefined
              }
            />
          </MarginBottom>
        )}
        {(!type || type === 'email') && (
          <Controller
            name="email"
            control={control}
            rules={{
              required: {value: type === 'email', message: 'Введите e-mail'},
              // eslint-disable-next-line
              pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,8})+$/,
            }}
            render={({field}) => (
              <MarginBottom $value={20} style={{width: '100%'}}>
                <Input
                  name={field.name}
                  value={field.value}
                  autoComplete="email"
                  size="l"
                  label="Email"
                  error={
                    errors.email
                      ? errors.email?.type === 'pattern'
                        ? 'Некорректный Email'
                        : errors.email?.message || 'Введите email'
                      : undefined
                  }
                  onChange={(event) => changeEmail(event, field)}
                />
              </MarginBottom>
            )}
          />
        )}
        <Controller
          name="password"
          control={control}
          rules={{required: 'Введите пароль'}}
          render={({field}) => (
            <MarginBottom $value={32} style={{width: '100%'}}>
              <Input
                name={field.name}
                value={field.value}
                type={showPass ? 'text' : 'password'}
                autoComplete="new-password"
                size="l"
                label="Пароль"
                error={errors.password?.message}
                onChange={(event) => changePass(event, field)}
                onFocus={() => clearErrors()}
                iconSuffix={showPass ? <PassShow /> : <PassHide />}
                onClickIconSuffix={() => setShowPass(!showPass)}
              />
            </MarginBottom>
          )}
        />

        <Button
          label="Войти"
          size="l"
          style={{width: '100%', marginBottom: '0.5rem'}}
          onClick={handleSubmit(onSubmit)}
        />
        <Button
          label="Создать новый аккаунт"
          size="l"
          style={{width: '100%'}}
          variant="outline"
          onClick={() => navigate(`/oauth/realms/${realm}/registration`)}
        />
        <MarginBottom $value={16} style={{width: '100%'}} />
        <RecoveryButton onClick={() => navigate(`/oauth/realms/${realm}/recovery-password`)}>
          Восстановить пароль
        </RecoveryButton>
      </LoginWindow>
    </Container>
  );
};
